import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import { fetchPendingExperiences, approveExperience, canUserReview, fetchCreatorWaitlist, fetchPendingSubmissions } from '../services/experiencesFirestore';
import { useExperiences } from '../context/ExperiencesContext';
import AuthPanel from './AuthPanel';
import ExperienceOrb from './ExperienceOrb';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #00f5ff, #ff00aa);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 24px;
  font-size: 0.95rem;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  min-height: 40vh;
`;

const CardWithActions = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ApproveButton = styled.button`
  background: linear-gradient(135deg, #00c853, #00e676);
  border: none;
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.95;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.p`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  font-size: 1.1rem;
  margin-top: 40px;
`;

const LoadError = styled.p`
  color: #ffb4b4;
  background: rgba(180, 40, 40, 0.2);
  border: 1px solid rgba(255, 120, 120, 0.45);
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 16px;
`;

const BlockedMessage = styled.p`
  color: #ff9800;
  text-align: center;
  padding: 24px;
  background: rgba(255, 152, 0, 0.1);
  border-radius: 12px;
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #f4f1fb;
  margin: 40px 0 8px;
`;

const SectionSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 16px;
  font-size: 0.9rem;
`;

const WaitlistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 720px;
`;

const WaitlistItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WaitlistName = styled.div`
  font-weight: 700;
  color: #f4f1fb;
`;

const WaitlistMeta = styled.div`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.72);
`;

const WaitlistLink = styled.a`
  font-size: 0.88rem;
  color: #00f0b0;
  word-break: break-all;
  text-decoration: none;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Favicon = styled.img`
  width: 16px;
  height: 16px;
  flex: none;
`;

const DomainName = styled.span`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.85);
  flex: none;
`;

const HeroThumb = styled.img`
  width: 96px;
  height: 54px;
  object-fit: cover;
  border-radius: 8px;
  margin-top: 6px;
`;

const Notes = styled.div`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.8);
  white-space: pre-wrap;
`;

function domainFromUrl(raw) {
  try {
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function ReviewLink({ href }) {
  if (!href) return null;
  const domain = domainFromUrl(href);
  return (
    <LinkRow>
      {domain && (
        <Favicon
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`}
          alt=""
        />
      )}
      {domain && <DomainName>{domain}</DomainName>}
      <WaitlistLink href={href} target="_blank" rel="noopener noreferrer">
        {href}
      </WaitlistLink>
    </LinkRow>
  );
}

function ReviewPage({
  onBack,
  onExperienceClick,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  canCreatePost,
  canReview
}) {
  const { user } = useAuth();
  const { refetch } = useExperiences();
  const [pending, setPending] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [loadErrors, setLoadErrors] = useState({ pending: '', submissions: '', waitlist: '' });

  useEffect(() => {
    if (!user) {
      setAllowed(false);
      setPending([]);
      setSubmissions([]);
      setWaitlist([]);
      setLoadErrors({ pending: '', submissions: '', waitlist: '' });
      setLoading(false);
      return;
    }
    canUserReview(user.uid).then((ok) => {
      setAllowed(ok);
      if (!ok) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadErrors({ pending: '', submissions: '', waitlist: '' });

      const loadList = (key, fetcher, setter) =>
        fetcher()
          .then(setter)
          .catch((err) => {
            console.error(err);
            setter([]);
            const detail = (err && (err.code || err.message)) || 'Something went wrong.';
            setLoadErrors((prev) => ({
              ...prev,
              [key]: `Could not load this list. ${detail}`,
            }));
          });

      Promise.all([
        loadList('pending', fetchPendingExperiences, setPending),
        loadList('submissions', fetchPendingSubmissions, setSubmissions),
        loadList('waitlist', fetchCreatorWaitlist, setWaitlist),
      ]).finally(() => setLoading(false));
    });
  }, [user]);

  const handleApprove = async (experienceId) => {
    setApprovingId(experienceId);
    try {
      await approveExperience(experienceId);
      setPending((prev) => prev.filter((e) => e.id !== experienceId));
      refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setApprovingId(null);
    }
  };

  if (!user) {
    return (
      <Container>
        <TopBar>
          <BackButton type="button" onClick={onBack}>← Back</BackButton>
          <AuthPanel
            onNavigateToSaved={onNavigateToSaved}
            onNavigateToRecommendations={onNavigateToRecommendations}
            onNavigateToCreate={onNavigateToCreate}
            onNavigateToMyUploads={onNavigateToMyUploads}
            onNavigateToReview={onNavigateToReview}
            canCreatePost={canCreatePost}
            canReview={canReview}
          />
        </TopBar>
        <Title>Review content</Title>
        <BlockedMessage>Sign in to access this page.</BlockedMessage>
      </Container>
    );
  }

  if (!allowed && !loading) {
    return (
      <Container>
        <TopBar>
          <BackButton type="button" onClick={onBack}>← Back</BackButton>
          <AuthPanel
            onNavigateToSaved={onNavigateToSaved}
            onNavigateToRecommendations={onNavigateToRecommendations}
            onNavigateToCreate={onNavigateToCreate}
            onNavigateToMyUploads={onNavigateToMyUploads}
            onNavigateToReview={onNavigateToReview}
            canCreatePost={canCreatePost}
            canReview={canReview}
          />
        </TopBar>
        <Title>Review content</Title>
        <BlockedMessage>You don’t have permission to review content. Only reviewers can access this page.</BlockedMessage>
      </Container>
    );
  }

  return (
    <Container>
      <TopBar>
        <BackButton type="button" onClick={onBack}>← Back</BackButton>
        <AuthPanel
          onNavigateToSaved={onNavigateToSaved}
          onNavigateToRecommendations={onNavigateToRecommendations}
          onNavigateToCreate={onNavigateToCreate}
          onNavigateToMyUploads={onNavigateToMyUploads}
          onNavigateToReview={onNavigateToReview}
          canCreatePost={canCreatePost}
          canReview={canReview}
        />
      </TopBar>
      <Title>Review content</Title>
      <Subtitle>Approve pending uploads so they appear on the home page.</Subtitle>

      {loading && <EmptyState>Loading…</EmptyState>}
      {loadErrors.pending && <LoadError>{loadErrors.pending}</LoadError>}
      {!loading && !loadErrors.pending && pending.length === 0 && (
        <EmptyState>No pending content to review.</EmptyState>
      )}
      {!loading && pending.length > 0 && (
        <Grid>
          {pending.map((exp, i) => (
            <CardWithActions key={exp.id}>
              <div style={{ width: '100%', maxWidth: 280 }}>
                <ExperienceOrb
                  experience={exp}
                  index={i}
                  onExperienceClick={onExperienceClick}
                  showSaveButton={false}
                />
              </div>
              <ApproveButton
                type="button"
                onClick={() => handleApprove(exp.id)}
                disabled={approvingId === exp.id}
              >
                {approvingId === exp.id ? 'Approving…' : 'Approve'}
              </ApproveButton>
            </CardWithActions>
          ))}
        </Grid>
      )}

      <SectionTitle>Submissions pending review</SectionTitle>
      <SectionSubtitle>
        Share-flow submissions with status pending_review. These do not appear on the home page until they are published separately.
      </SectionSubtitle>
      {loadErrors.submissions && <LoadError>{loadErrors.submissions}</LoadError>}
      {!loading && !loadErrors.submissions && submissions.length === 0 && (
        <EmptyState style={{ marginTop: 0 }}>No submissions waiting for review.</EmptyState>
      )}
      {!loading && submissions.length > 0 && (
        <WaitlistList>
          {submissions.map((entry) => (
            <WaitlistItem key={entry.id}>
              <WaitlistName>{entry.title}</WaitlistName>
              <WaitlistMeta>
                {entry.type === 'creator' ? 'Creator' : 'User'}
                {entry.creatorName ? ` · ${entry.creatorName}` : ''}
                {entry.email ? ` · ${entry.email}` : ''}
                {entry.createdAt
                  ? ` · ${entry.createdAt.toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}`
                  : ''}
              </WaitlistMeta>
              {entry.type === 'creator' && entry.heroImageUrl && (
                <HeroThumb src={entry.heroImageUrl} alt="" />
              )}
              {entry.type === 'user' && (
                <Notes>Notes: {entry.notes || '—'}</Notes>
              )}
              <ReviewLink href={entry.link} />
            </WaitlistItem>
          ))}
        </WaitlistList>
      )}

      <SectionTitle>Creator waitlist</SectionTitle>
      <SectionSubtitle>
        Interest form submissions while creator upload is gated. Also visible in Firebase Console →
        Firestore → creator_waitlist.
      </SectionSubtitle>
      {loadErrors.waitlist && <LoadError>{loadErrors.waitlist}</LoadError>}
      {!loading && !loadErrors.waitlist && waitlist.length === 0 && (
        <EmptyState style={{ marginTop: 0 }}>No creator waitlist entries yet.</EmptyState>
      )}
      {!loading && waitlist.length > 0 && (
        <WaitlistList>
          {waitlist.map((entry) => (
            <WaitlistItem key={entry.id}>
              <WaitlistName>{entry.name}</WaitlistName>
              <WaitlistMeta>
                {entry.email}
                {entry.createdAt
                  ? ` · ${entry.createdAt.toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}`
                  : ''}
              </WaitlistMeta>
              <ReviewLink href={entry.experienceUrl} />
            </WaitlistItem>
          ))}
        </WaitlistList>
      )}
    </Container>
  );
}

export default ReviewPage;
